import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BadgeCheck, Heart, MapPin } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProgressBar from "@/components/ProgressBar";
import CampaignCard from "@/components/CampaignCard";
import DonationPanel from "@/components/DonationPanel";
import PixModal from "@/components/PixModal";
import NotFound from "@/pages/NotFound";
import { fetchCampaign, fetchCampaigns, formatBRL } from "@/lib/api";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
};

function RecommendedRow({ slug }) {
  const [others, setOthers] = useState([]);
  useEffect(() => {
    fetchCampaigns()
      .then((list) => setOthers(list.filter((x) => x.slug !== slug).slice(0, 3)))
      .catch(() => {});
  }, [slug]);
  if (!others.length) return null;
  return (
    <motion.section {...fadeUp} transition={{ duration: 0.5 }} className="mt-16">
      <h2 className="font-display text-2xl font-extrabold text-[#261C14] sm:text-3xl">
        Outras histórias também precisam de você!
      </h2>
      <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {others.map((c2, i) => (
          <CampaignCard key={c2.slug} campaign={c2} index={i} />
        ))}
      </div>
    </motion.section>
  );
}

export default function Campaign() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [tab, setTab] = useState("sobre");
  const [expanded, setExpanded] = useState(false);
  const [pixDonation, setPixDonation] = useState(null);
  const [liked, setLiked] = useState(false);

  const load = () => fetchCampaign(slug).then(setData).catch(() => setFailed(true));
  useEffect(() => {
    setData(null);
    setFailed(false);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (failed) return <NotFound />;
  if (!data)
    return (
      <div className="min-h-screen">
        <Header />
        <div className="mx-auto max-w-7xl animate-pulse px-4 py-20 sm:px-6">
          <div className="h-8 w-2/3 rounded bg-[#F3EAE0]" />
          <div className="mt-6 h-64 rounded-2xl bg-[#F3EAE0]" />
        </div>
      </div>
    );

  const c = data;
  const tabs = [
    { id: "sobre", label: "Sobre a causa", testid: "campaign-tabs-about" },
    { id: "atualizacoes", label: `Atualizações (${c.updates.length})`, testid: "campaign-tabs-updates" },
    { id: "quem-ajudou", label: `Quem ajudou (${c.donations.length})`, testid: "campaign-tabs-donors" },
    { id: "prestacao", label: "Prestação de contas", testid: "campaign-tabs-accounts" },
  ];
  const visibleStory = expanded ? c.story : c.story.slice(0, 2);

  return (
    <div className="min-h-screen">
      <Header />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-[#6E6259] transition hover:text-[#FF6D47]">
          <ArrowLeft className="h-4 w-4" /> Voltar para a home
        </Link>

        <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="mt-6">
          <div className="flex flex-wrap items-center gap-3 text-sm font-bold">
            <span className="rounded-full bg-[#FFF0EB] px-3 py-1 text-xs font-extrabold text-[#FF6D47]">{c.category}</span>
            <span className="flex items-center gap-1 text-[#6E6259]">
              <MapPin className="h-4 w-4" /> {c.location}
            </span>
            <span className="text-[#9E9085]">ID: {c.campaign_number}</span>
          </div>
          <div className="mt-4 flex items-start justify-between gap-6">
            <h1 className="font-display max-w-3xl text-3xl font-extrabold leading-tight text-[#261C14] sm:text-4xl lg:text-5xl">
              {c.title}
            </h1>
            <button
              data-testid="campaign-heart-btn"
              onClick={() => setLiked(!liked)}
              aria-label="Enviar coração para esta causa"
              className="mt-2 grid h-12 w-12 shrink-0 place-items-center rounded-full border-2 border-[#EFE9DF] bg-white transition hover:border-[#E11D48]"
            >
              <motion.span animate={{ scale: liked ? [1, 1.4, 1.15] : 1 }} className="grid place-items-center">
                <Heart className={`h-5 w-5 ${liked ? "fill-[#E11D48] text-[#E11D48]" : "text-[#9E9085]"}`} />
              </motion.span>
            </button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {["Mais amada da semana", "Causa verificada", "Pix via LivePix"].map((b) => (
              <span
                key={b}
                className="flex items-center gap-1.5 rounded-full bg-[#F7F3ED] px-3 py-1.5 text-xs font-extrabold text-[#6E6259]"
              >
                <BadgeCheck className="h-3.5 w-3.5 text-[#FF6D47]" /> {b}
              </span>
            ))}
          </div>
        </motion.div>

        <div className="mt-8 grid gap-10 lg:grid-cols-12">
          <div className="min-w-0 space-y-8 lg:col-span-8">
            <motion.img
              {...fadeUp}
              transition={{ duration: 0.6 }}
              src={c.cover}
              alt={c.title}
              className="aspect-video w-full rounded-2xl object-cover shadow-lg"
            />

            <div className="flex gap-1 overflow-x-auto border-b border-[#EFE9DF]">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  data-testid={t.testid}
                  onClick={() => setTab(t.id)}
                  className={`whitespace-nowrap border-b-[3px] px-4 pb-3 pt-2 text-sm font-extrabold transition ${
                    tab === t.id ? "border-[#FF6D47] text-[#FF6D47]" : "border-transparent text-[#6E6259] hover:text-[#261C14]"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === "sobre" && (
              <div className="space-y-5">
                <div className="space-y-4 text-base leading-relaxed text-[#3D3129]">
                  {visibleStory.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
                {c.story.length > 2 && (
                  <button
                    onClick={() => setExpanded(!expanded)}
                    data-testid="story-toggle-btn"
                    className="font-bold text-[#FF6D47] underline-offset-4 hover:underline"
                  >
                    {expanded ? "Ler menos" : "Ler mais"}
                  </button>
                )}

                <div className="flex items-center gap-4 rounded-2xl border border-[#EFE9DF] bg-white p-5">
                  <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[#FF6D47] text-white">
                    <Heart className="h-5 w-5 fill-white" />
                  </span>
                  <div>
                    <p className="font-display font-extrabold text-[#261C14]">{c.organizer.name}</p>
                    <p className="text-sm font-semibold text-[#6E6259]">{c.organizer.subtitle} · 100% verificado</p>
                  </div>
                </div>

                <p className="text-xs leading-relaxed text-[#9E9085]">
                  AVISO LEGAL: O conteúdo desta campanha é de responsabilidade do seu criador e não representa a opinião da
                  plataforma PetLovers. Doações processadas com segurança via Pix/LivePix.
                </p>
              </div>
            )}

            {tab === "atualizacoes" && (
              <div className="space-y-4">
                {c.updates.length === 0 && <p className="font-bold text-[#9E9085]">Ainda não há atualizações nesta campanha.</p>}
                {c.updates.map((u) => (
                  <div key={u.date} className="rounded-2xl border border-[#EFE9DF] bg-white p-5">
                    <p className="text-xs font-extrabold uppercase tracking-wider text-[#FF6D47]">{u.date}</p>
                    <p className="mt-1.5 text-[#3D3129]">{u.text}</p>
                  </div>
                ))}
              </div>
            )}

            {tab === "quem-ajudou" && (
              <div className="space-y-3">
                {c.donations.length === 0 && <p className="font-bold text-[#9E9085]">Seja o primeiro a doar nesta causa!</p>}
                {c.donations.map((d) => (
                  <div key={d.donation_id} className="flex items-start gap-4 rounded-2xl border border-[#EFE9DF] bg-white p-5">
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#FFF0EB] text-sm font-extrabold text-[#FF6D47]">
                      {(d.donor_name || "A").slice(0, 2).toUpperCase()}
                    </span>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="font-extrabold text-[#261C14]">{d.donor_name}</p>
                        <p className="font-display font-extrabold text-[#FF6D47]">{formatBRL(d.amount_cents)}</p>
                      </div>
                      {d.message && <p className="mt-1 text-sm text-[#6E6259]">“{d.message}”</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === "prestacao" && (
              <div className="space-y-4 rounded-2xl border border-[#EFE9DF] bg-white p-6">
                <p className="font-display text-xl font-bold text-[#261C14]">Para onde vai cada doação</p>
                {[
                  ["Ração e manutenção do abrigo", 45],
                  ["Cuidado veterinário e medicamentos", 30],
                  ["Reformas e novos abrigos", 25],
                ].map(([label, pct]) => (
                  <div key={label}>
                    <div className="flex justify-between text-sm font-bold">
                      <span className="text-[#6E6259]">{label}</span>
                      <span className="text-[#FF6D47]">{pct}%</span>
                    </div>
                    <ProgressBar pct={pct} className="mt-1.5 h-2" />
                  </div>
                ))}
                <p className="text-xs text-[#9E9085]">
                  Relatório mensal de prestação de contas enviado a todos os apoiadores por e-mail.
                </p>
              </div>
            )}
          </div>

          <div className="min-w-0 lg:col-span-4">
            <DonationPanel campaign={c} onDonationCreated={setPixDonation} />
          </div>
        </div>

        <RecommendedRow slug={c.slug} />
      </main>
      <Footer />

      {pixDonation && (
        <PixModal
          donation={pixDonation}
          onClose={() => {
            setPixDonation(null);
            load();
          }}
        />
      )}
    </div>
  );
}
